'use client';

import { ThumbsUp, ThumbsUpFilled } from 'phosphor-icons';
import { useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import {
  TimeFrame,
  calculateTimeAgo,
  getEditedTimeAgo,
} from '../../../utils/time';
import { Badge } from '../../atoms/Badge';
import Button from '../../atoms/Button';
import { Textarea } from '../../atoms/Textarea';
import { ConfirmationPopup } from '../../molecules/ConfirmationPopup';
import { StarsV2 } from '../../molecules/Stars.v2';
import { ReportReviewModal } from '../ReportReviewModal';
import { ReviewFormData, WriteReviewModal } from '../WriteReviewModal';
import { UserReviewProps } from './UserReview.types';

export const UserReview = ({
  id,
  ownedDate,
  // canSubmitReview, TODO: Uncomment when Version is ready
  microcopy,
  stars,
  gifted,
  version,
  title,
  review,
  publishedDate,
  editedDate,
  helpfulVotes,
  canReportReview,
  canHelpfulVotes = false,
  canEdit,
  canDelete,
  canReply,
  reply,
  writeReviewModal,
  isMultiPackage,
  onSubmitReport,
  onHelpfulVotesReview,
  onEditReview,
  onHelpfulVotesReply,
  onEditReply,
  onDeleteReply,
  onSubmitReply,
  onEmailPublisher,
  onDeleteReview,
  className,
}: UserReviewProps) => {
  const [openReplyBox, setOpenReplyBox] = useState(false);
  const [replyField, setReplyField] = useState('');

  const getDateMicrocopy = (frame: TimeFrame): string => {
    switch (frame) {
      case 'a few seconds ago':
        return microcopy.secondsAgo;
      case 'minute':
        return microcopy.minuteAgo;
      case 'minutes':
        return microcopy.minutesAgo;
      case 'hour':
        return microcopy.hourAgo;
      case 'hours':
        return microcopy.hoursAgo;
      case 'day':
        return microcopy.dayAgo;
      case 'days':
        return microcopy.daysAgo;
      case 'month':
        return microcopy.monthAgo;
      case 'months':
        return microcopy.monthsAgo;
      case 'year':
        return microcopy.yearAgo;
      case 'years':
        return microcopy.yearsAgo;
      default:
        return '';
    }
  };

  const hasReviewActions = canEdit || canDelete || canReportReview || canReply;

  const hasReplyReviewActions =
    reply?.canEdit || reply?.canDelete || reply?.canHelpfulVotes;

  const renderHeader = (
    <header className="flex w-full flex-wrap md:items-center md:justify-between">
      <div className="order-1 block w-full justify-between md:order-1 md:flex md:w-auto md:justify-start">
        <div className="flex items-start">
          <StarsV2
            className="items-start"
            width={20}
            height={20}
            ariaLabel={microcopy.stars.ariaLabel}
            variant="blue"
            {...stars}
          />
        </div>

        {gifted && (
          <span className="ml-0 mt-3.5 inline-block text-sm md:ml-3 md:mt-0">
            <Badge text={microcopy.gifted} />
          </span>
        )}
      </div>

      <span className="order-3 mt-3.5 inline-block w-full text-sm md:order-2 md:mt-0 md:w-auto">
        {isMultiPackage ? microcopy.multiPackageAsset : ''}
      </span>
    </header>
  );

  /* The invisible class should be removed when implementing */
  const renderThumbsUpIcon = (
    canVote: boolean,
    helpfulVotes: number,
    action: () => void
  ) =>
    canVote && (
      <button
        className="group invisible flex items-center p-2 text-sm"
        onClick={action}
        aria-label={microcopy.helpfulVotesAriaLabel}
        disabled={!canVote}
      >
        {helpfulVotes}

        <ThumbsUp
          color="#4268e6"
          size={20}
          weight="bold"
          className="ml-1 block group-hover:hidden"
        />

        <ThumbsUpFilled
          color="#4268e6"
          size={20}
          className="ml-1 hidden group-hover:block"
        />
      </button>
    );

  const renderEditAndDelete = () => {
    const lastUpdatedAt = writeReviewModal.data?.lastUpdatedAt || '';
    const editData = {
      id: writeReviewModal.data?.id || '',
      rating: writeReviewModal.data?.rating ?? stars.value,
      title: writeReviewModal.data?.title ?? title,
      body: writeReviewModal.data?.body ?? review,
      productMetadata: writeReviewModal.data?.productMetadata || {
        upm: {
          packageVersion: writeReviewModal.version || version,
        },
      },
      lastUpdatedAt,
    };

    const handleEditReview = async (
      _productId: string,
      formData: ReviewFormData
    ) => {
      return onEditReview(id, formData, lastUpdatedAt);
    };

    return (
      <>
        {canEdit && (
          <WriteReviewModal
            productId={writeReviewModal.productId}
            title={writeReviewModal.title}
            imageUrl={writeReviewModal.imageUrl}
            version={writeReviewModal.version || version}
            ownedDate={writeReviewModal.ownedDate}
            isMultiPackage={writeReviewModal.isMultiPackage}
            data={editData}
            microcopy={microcopy.writeReviewModal}
            onEmailPublisher={onEmailPublisher}
            onSubmitReview={handleEditReview}
            reviewModalTrigger={
              <Button.v2
                size="xsmall"
                shape="square"
                variant="tertiary-blue-monochrome"
                iconLeft="PencilSimple"
                label={microcopy.edit}
              />
            }
            className="w-full md:mb-0 md:w-auto"
          />
        )}
        {canDelete && (
          <ConfirmationPopup
            id={id}
            title={microcopy.confirmationModal.title}
            content={microcopy.confirmationModal.content}
            onDeleteReview={() => onDeleteReview(id)}
            trigger={
              <Button.v2
                size="xsmall"
                shape="square"
                variant="tertiary-red-monochrome"
                iconLeft="Trash"
                label={microcopy.delete}
              />
            }
          />
        )}
      </>
    );
  };

  const renderReviewActions = () => {
    if (hasReviewActions || canHelpfulVotes) {
      return (
        <div
          className={`border-1 flex ${canHelpfulVotes ? 'justify-between' : 'justify-end'} border-t border-grey-200 pt-2`}
        >
          {canHelpfulVotes &&
            renderThumbsUpIcon(
              canHelpfulVotes,
              helpfulVotes || 0,
              onHelpfulVotesReview ?? (() => {})
            )}
          <div className="flex items-center gap-3 text-sm">
            {canReply && !openReplyBox && (
              <Button.v2
                size="xsmall"
                shape="square"
                variant="tertiary-blue-monochrome"
                label={microcopy.reply}
                onClick={() => setOpenReplyBox(!openReplyBox)}
                iconLeft="ArrowUDownLeft"
                data-testid="user-review-reply-button"
              />
            )}

            {ownedDate ? renderEditAndDelete() : null}

            {canReportReview && (
              <ReportReviewModal
                className="items-center"
                microcopy={{
                  title: microcopy.reviewModal.title,
                  placeholder: microcopy.reviewModal.placeholder,
                  close: microcopy.reviewModal.close,
                  primaryCta: microcopy.reviewModal.primaryCta,
                  secondaryCta: microcopy.reviewModal.secondaryCta,
                  triggerLabel: microcopy.reportReview,
                  description: microcopy.reviewModal.description,
                  descriptionTitle: microcopy.reviewModal.descriptionTitle,
                  reportType: {
                    title: microcopy.reviewModal.reportType?.title,
                    options: [
                      ...microcopy.reviewModal.reportType?.options.map(
                        (option) => ({
                          label: option.label,
                          value: option.value,
                        })
                      ),
                    ],
                  },
                  support: {
                    title: microcopy.reviewModal.support.title,
                    description: microcopy.reviewModal.support.description,
                  },
                  confirmationMessage: {
                    title: microcopy.reviewModal.confirmationMessage.title,
                    subtitle:
                      microcopy.reviewModal.confirmationMessage.subtitle,
                  },
                  serverMessage: {
                    title: microcopy.reviewModal.serverMessage?.title || '',
                    description:
                      microcopy.reviewModal.serverMessage?.description || '',
                    dismiss: microcopy.reviewModal.serverMessage?.dismiss || '',
                  },
                }}
                reviewId={id}
                onSubmitReport={(
                  reviewId: string,
                  reportData: { reportReview: string; reportType: string }
                ) => onSubmitReport(reviewId, reportData)}
              />
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderReplyActions = (
    <div
      className={`border-1 flex ${reply?.canHelpfulVotes ? 'justify-between' : 'justify-end'} border-t border-grey-200 pt-2`}
    >
      {reply?.canHelpfulVotes &&
        renderThumbsUpIcon(
          true,
          reply?.helpfulVotes || 0,
          onHelpfulVotesReply ?? (() => {})
        )}
      <div className="flex gap-3 text-sm">
        {reply?.canEdit && (
          <Button.v2
            size="xsmall"
            shape="square"
            variant="tertiary-blue-monochrome"
            iconLeft="PencilSimple"
            label={microcopy.edit}
            onClick={onEditReply}
          />
        )}
        {reply?.canDelete && (
          <Button.v2
            size="xsmall"
            shape="square"
            variant="tertiary-red-monochrome"
            iconLeft="Trash"
            label={microcopy.delete}
            onClick={onDeleteReply}
          />
        )}
      </div>
    </div>
  );

  const renderWriteReplyTextarea = () => {
    return (
      openReplyBox && (
        <div className="w-full rounded-b border border-t-0 border-solid border-grey-200 bg-grey-100 p-4 pb-2 text-sm text-grey-600">
          {microcopy.write}
          <Textarea
            className="my-2 h-28 w-full"
            value={replyField}
            onChange={(e) => setReplyField(e.target.value)}
            id="replyFieldTextarea"
            name="replyFieldTextarea"
            data-testid="user-review-reply-textarea"
            placeholder={microcopy.writePlaceholder}
          />
          <Button.v2
            className="mr-3 p-1.5"
            onClick={() => onSubmitReply(replyField)}
            label={microcopy.post}
            size="xsmall"
            variant="tertiary-grey-monochrome"
            data-testid="user-review-post-reply-button"
            disabled={!replyField.length}
          />
          <Button.v2
            onClick={() => {
              setReplyField('');
              setOpenReplyBox(false);
            }}
            size="xsmall"
            shape="square"
            variant="tertiary-grey-monochrome"
            label={microcopy.cancel}
          />
        </div>
      )
    );
  };

  const renderDates = (
    publishedDate: string,
    editedDate: string,
    isReplyContext: boolean = false
  ) => {
    const publishedDateTimeAgo = calculateTimeAgo(new Date(publishedDate));

    const publishedReplyDateTimeAgo =
      isReplyContext && reply?.publishedDate
        ? calculateTimeAgo(new Date(reply.publishedDate))
        : undefined;

    return (
      <div
        className={`flex flex-wrap gap-2 ${hasReviewActions || canHelpfulVotes ? 'mb-3.5' : ''}`}
      >
        <span className="order-1 pr-3 text-sm text-grey-600">
          {interpolateStringWithData(
            `${microcopy.published} ${getDateMicrocopy(
              isReplyContext && publishedReplyDateTimeAgo
                ? publishedReplyDateTimeAgo.frame
                : publishedDateTimeAgo.frame
            )}`,
            {
              time: isReplyContext
                ? publishedReplyDateTimeAgo?.time ?? ''
                : publishedDateTimeAgo?.time ?? '',
            }
          )}
        </span>
        {editedDate && (
          <span className="order-2 text-sm text-grey-600">
            {interpolateStringWithData(
              `${microcopy.edited} ${getDateMicrocopy(calculateTimeAgo(new Date(editedDate)).frame)}`,
              {
                time: getEditedTimeAgo(new Date(editedDate)).time,
              }
            )}
          </span>
        )}
      </div>
    );
  };

  const renderReplyBox = () => {
    return (
      reply?.body && (
        <div
          className={`w-full rounded-b border border-t-0 border-solid border-grey-200 bg-grey-100 p-4 ${hasReplyReviewActions ? ' pb-2' : ''}`}
        >
          <span className="pr-3 text-sm text-grey-600">
            {microcopy.replyFromPublisher}
          </span>
          <p
            className="my-3.5 whitespace-pre-line text-sm"
            data-testid="user-review-reply-body"
          >
            {reply?.body}
          </p>
          {reply?.editedDate &&
            renderDates(reply.publishedDate, reply?.editedDate, true)}
          {(reply?.canEdit || reply?.canDelete || reply?.canHelpfulVotes) &&
            renderReplyActions}
        </div>
      )
    );
  };

  return (
    <article className={c('w-full')}>
      <div
        className={c(
          'w-full rounded-t border border-solid border-grey-200 p-4 pb-2 text-grey-900',
          className
        )}
      >
        {renderHeader}
        <h3 className="my-3.5 text-sm font-bold">{title}</h3>
        <p
          data-testid="user-review-body"
          className="mb-3.5 whitespace-pre-line text-sm"
        >
          {review}
        </p>
        {renderDates(publishedDate, editedDate || '')}

        {renderReviewActions()}
      </div>

      {renderWriteReplyTextarea()}

      {renderReplyBox()}
    </article>
  );
};
